/**
 * @prettier
 */
import { IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import moment from 'moment';
import { MUIDataTableColumn } from 'mui-datatables';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { downloadOrderInvoiceApi } from 'src/api/letseatmanager/invoice/downloadOrderInvoiceApi';
import { findOrderInvoicesApi, OrderInvoiceVm } from 'src/api/letseatmanager/invoice/findOrderInvoicesApi';
import { FilterState, FromToFilter } from 'src/components/input/FromToFilter';
import { SecuredAndSubscribedPage } from 'src/components/page/SecuredAndSubscribedPage';
import { Table } from 'src/components/Table';
import { UpdatingContentProgress } from 'src/components/UpdatingContentProgress';
import { RolePermissions } from 'src/constants/RolePermission';
import { translate } from 'src/i18n/translate';
import { useFormatAsRestaurantCurrencyNumber } from 'src/services/restaurant/useFormatAsRestaurantCurrencyNumber';
import { InvoiceId } from 'src/types/Id';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { formatDateTimeString } from 'src/utils/date/formatDateTimeString';
import { useSelector } from 'src/utils/react/useSelector';

export function OrderInvoicesPage(): React.ReactElement {
    const formatAsCurrencyNumber = useFormatAsRestaurantCurrencyNumber();

    const [loading, setLoading] = useState(false);
    const [orderInvoices, setOrderInvoices] = useState<Array<OrderInvoiceVm>>([]);
    const [filter, setFilter] = useState<FilterState>({
        from: moment().startOf('isoWeek').toDate(),
        to: moment().toDate(),
    });

    const restaurantId = useSelector((state) => state.app.restaurantId);

    useEffect(() => {
        load();
    }, [filter]);

    const load = async () => {
        setLoading(true);
        const response = await findOrderInvoicesApi({
            restaurantId,
            from: filter.from,
            to: filter.to,
        });
        setLoading(false);
        if (!response.ok) return alertKnownErrorOrSomethingWentWrong(response);
        setOrderInvoices(response.data);
    };

    const downloadOrderInvoice = async ({ invoiceId, shortOrderId }: { invoiceId: InvoiceId; shortOrderId: string }) => {
        setLoading(true);
        const responsePdf = await downloadOrderInvoiceApi({
            restaurantId,
            invoiceId,
            documentType: 'pdf',
        });
        if (!responsePdf.ok) return alertKnownErrorOrSomethingWentWrong(responsePdf);
        downloadFile(responsePdf.data.uri, shortOrderId);

        const responseXml = await downloadOrderInvoiceApi({
            restaurantId,
            invoiceId,
            documentType: 'xml',
        });
        setLoading(false);
        if (!responseXml.ok) return alertKnownErrorOrSomethingWentWrong(responseXml);

        downloadFile(responseXml.data.uri, shortOrderId);
    };

    const downloadFile = (uri: string, shortOrderId: string) => {
        const downloadAction = document.createElement('a');

        downloadAction.href = uri;
        downloadAction.download = `${translate('Invoice')} ${shortOrderId}`;

        document.body?.appendChild(downloadAction);
        downloadAction.click();
        document.body?.removeChild(downloadAction);
    };

    const handleChangeFilter = (filter: { from: Date; to: Date }) => setFilter(filter);

    return (
        <SecuredAndSubscribedPage title={translate('Order Invoices')} rolePermission={RolePermissions.ORDER_INVOICES_PAGE}>
            <Grid container spacing={5}>
                <Grid item xs={12}>
                    <FromToFilter filter={filter} onChangeFilter={handleChangeFilter} />
                </Grid>
                <Grid item xs={12}>
                    <div style={{ position: 'relative', paddingBottom: 3 }}>
                        <UpdatingContentProgress loading={loading} bottom />
                        <Table
                            data={orderInvoices.map((invoice) => ({
                                invoiceId: invoice.invoiceId,
                                shortOrderId: invoice.shortOrderId,
                                businessName: invoice.businessName,
                                amount: formatAsCurrencyNumber(invoice.amount),
                                createdAt: formatDateTimeString(invoice.createdAt),
                                downloadInvoice: { invoiceId: invoice.invoiceId, shortOrderId: invoice.shortOrderId, downloadAvailable: !!invoice.facturamaInvoiceId },
                            }))}
                            columns={
                                [
                                    {
                                        name: 'invoiceId',
                                        label: '',
                                        options: {
                                            display: 'excluded',
                                            filter: false,
                                        },
                                    },
                                    {
                                        name: 'shortOrderId',
                                        label: '',
                                        options: {
                                            label: translate('Order Id'),
                                            filter: false,
                                        },
                                    },
                                    {
                                        name: 'businessName',
                                        label: translate('Business Name'),
                                        options: {
                                            filter: false,
                                        },
                                    },
                                    {
                                        name: 'amount',
                                        label: translate('Amount'),
                                        options: {
                                            filter: false,
                                            searchable: false,
                                        },
                                    },
                                    {
                                        name: 'createdAt',
                                        label: translate('Created At'),
                                        options: {
                                            filter: false,
                                            searchable: false,
                                        },
                                    },
                                    {
                                        name: 'downloadInvoice',
                                        label: translate('Download'),
                                        options: {
                                            filter: false,
                                            searchable: false,
                                            customBodyRender: (invoice) =>
                                                invoice.downloadAvailable && (
                                                    <IconButton onClick={() => downloadOrderInvoice(invoice)} disabled={loading}>
                                                        <CloudDownloadIcon />
                                                    </IconButton>
                                                ),
                                        },
                                    },
                                ] as Array<MUIDataTableColumn>
                            }
                            options={{
                                responsive: 'standard',
                                tableBodyMaxHeight: '500px',
                                selectableRows: 'none',
                                filterType: 'checkbox',
                                rowsPerPage: 100,

                                // filterList: [[], [], [], [], ['MEXICO']],
                            }}
                        />
                    </div>
                </Grid>
            </Grid>
        </SecuredAndSubscribedPage>
    );
}
